// Human labels for enums, shared by every dashboard and the public site.
// Typed by the enum they cover so a new enum value is a compile error here.
import type { Availability } from '../candidate.ts';
import type { COMPANY_STAGES } from '../company.ts';
import type { CuratorStatus } from '../curator.ts';
import type {
  CandidateStatus,
  COMPANY_STATUSES,
  CONSENT_STATES,
  CURATOR_DECISIONS,
  EmploymentType,
  RoleStatus,
  Seniority,
  SkillCategory,
} from '../enums.ts';
import type { RankingWeights } from '../ranking.ts';
import type { RegionTag } from '../regions.ts';

export const EMPLOYMENT_LABELS: Record<EmploymentType, string> = {
  full_time_employee: 'Full-time employee',
  contractor: 'Contractor',
  part_time: 'Part-time',
};

export const COMPANY_STAGE_LABELS: Record<(typeof COMPANY_STAGES)[number], string> = {
  pre_seed: 'Pre-seed',
  seed: 'Seed',
  series_a: 'Series A',
  series_b_plus: 'Series B+',
  bootstrapped: 'Bootstrapped',
  public: 'Public',
  other: 'Other',
};

export const SKILL_CATEGORY_LABELS: Record<SkillCategory, string> = {
  design: 'Design',
  product: 'Product',
  data: 'Data and analytics',
  language: 'Programming languages',
  framework: 'Frameworks',
  tool: 'Tools and software',
  domain: 'Domain knowledge',
  support: 'Customer support',
  sales: 'Sales',
  marketing: 'Marketing and content',
  operations: 'Operations, HR and legal',
  finance: 'Finance and accounting',
  healthcare: 'Healthcare',
  education: 'Education and training',
  soft: 'Soft skills',
};

export const SENIORITY_LABELS: Record<Seniority, string> = {
  junior: 'Junior',
  mid: 'Mid-level',
  senior: 'Senior',
  lead: 'Lead',
  principal: 'Principal',
};

export const ROLE_STATUS_LABELS: Record<RoleStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  matching: 'Matching',
  shortlisted: 'Shortlisted',
  introduced: 'Introduced',
  closed: 'Closed',
};

// Candidate-facing wording (web profile, digest, consent flow): friendlier,
// spells out what the status means for the person it's shown to.
export const CANDIDATE_STATUS_LABELS_PUBLIC: Record<CandidateStatus, string> = {
  new: 'Awaiting screening',
  screened: 'Screened',
  active: 'Active — being matched',
  paused: 'Paused',
  placed: 'Placed',
  withdrawn: 'Withdrawn',
};

// Curator-facing wording (admin dashboard): short, internal, scannable in a
// table. Split from the public map after OPEN_QUESTIONS Q38 — a Phase 9
// label-deduplication pass had accidentally merged the two and changed
// curator-visible text as a side effect.
export const CANDIDATE_STATUS_LABELS_INTERNAL: Record<CandidateStatus, string> = {
  new: 'New',
  screened: 'Screened',
  active: 'Active',
  paused: 'Paused',
  placed: 'Placed',
  withdrawn: 'Withdrawn',
};

// Curator activity log: what the curator did to a candidate's status…
const CURATOR_STATUS_CHANGE: Record<CandidateStatus, string> = {
  new: 'Moved back to new',
  screened: 'Screened',
  active: 'Activated',
  paused: 'Paused',
  placed: 'Marked placed',
  withdrawn: 'Withdrawn',
};

/** One line in the curator's activity log for a candidate status change. */
export function statusChangeLabel(c: {
  from: CandidateStatus;
  to: CandidateStatus;
  changedByName: string | null;
  byCandidate: boolean;
}): string {
  if (!c.byCandidate) return `${CURATOR_STATUS_CHANGE[c.to]} by ${c.changedByName ?? 'a curator'}`;
  // …and what the candidate did themselves (profile page or digest unsubscribe).
  const text =
    c.to === 'paused'
      ? 'Paused matching'
      : c.to === 'withdrawn'
        ? 'Withdrew from matching'
        : c.from === 'placed'
          ? 'Looked for roles again after placement'
          : c.to === 'new'
            ? 'Rejoined, back in the screening queue'
            : 'Rejoined matching';
  return `${text} · by the candidate`;
}

export const AVAILABILITY_LABELS: Record<Availability, string> = {
  immediately: 'Immediately',
  within_1_month: 'Within a month',
  within_3_months: 'Within three months',
  exploring: 'Just exploring',
};

export const REGION_TAG_LABELS: Record<RegionTag, string> = {
  WORLDWIDE: 'Worldwide',
  EU: 'European Union',
  LATAM: 'Latin America',
  APAC: 'Asia-Pacific',
  AMERICAS: 'Americas',
};

export const RANKING_WEIGHT_LABELS: Record<keyof RankingWeights, string> = {
  roleType: 'Role type match',
  requiredSkills: 'Required skills coverage',
  niceToHaveSkills: 'Nice-to-have skills coverage',
  seniority: 'Seniority (exact = full, one step = half)',
  timezoneOverlap: 'Timezone overlap',
  salary: 'Salary band',
  industryWorkedIn: 'Industry — worked in',
  industryInterestedIn: 'Industry — interested in',
  availabilityBonus: 'Availability bonus (immediately / ≤ 1 month)',
  minScore: 'Minimum score to create a match',
  workdayHours: 'Working day length used for overlap (hours)',
};

/** Label lookup for values typed as plain strings (API rows). Falls back to the raw value. */
export function label<K extends string>(
  map: Record<K, string>,
  key: string | null | undefined,
): string {
  if (!key) return '—';
  return (map as Record<string, string>)[key] ?? key;
}

export const COMPANY_STATUS_LABELS: Record<(typeof COMPANY_STATUSES)[number], string> = {
  pending: 'Pending',
  active: 'Active',
  suspended: 'Suspended',
};

export const CURATOR_DECISION_LABELS: Record<(typeof CURATOR_DECISIONS)[number], string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  rejected: 'Rejected',
};

/** `awaiting` is a client-side state: consent asked, no answer yet. */
export const CONSENT_LABELS: Record<(typeof CONSENT_STATES)[number] | 'awaiting', string> = {
  pending: 'Not answered',
  awaiting: 'Awaiting',
  yes: 'Said yes',
  no: 'Said no',
};

/** job_run.status and email_log.status. */
export type RunStatus = 'running' | 'ok' | 'sent' | 'logged' | 'failed';
export const RUN_STATUS_LABELS: Record<RunStatus, string> = {
  running: 'Running',
  ok: 'OK',
  sent: 'Sent',
  logged: 'Logged',
  failed: 'Failed',
};

/**
 * One line in the curator's activity log for a company status change, or
 * null for the move to active (the sanity-check and fee lines already say
 * so). `changedByName` null means the system did it.
 */
export function companyStatusChangeLabel(c: {
  from: (typeof COMPANY_STATUSES)[number];
  to: (typeof COMPANY_STATUSES)[number];
  changedByName: string | null;
}): string | null {
  const by = c.changedByName ?? 'a curator';
  if (c.to === 'suspended') return `Account suspended by ${by}`;
  if (c.from === 'suspended')
    return c.to === 'active'
      ? `Suspension lifted by ${by}, account active again`
      : `Suspension lifted by ${by}, back to pending until both gates are done`;
  return null;
}

export const CURATOR_STATUS_LABELS: Record<CuratorStatus, string> = {
  active: 'Active',
  invited: 'Invited',
  expired: 'Invite expired',
  deactivated: 'Deactivated',
};
